import { useSelector } from 'react-redux';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';
import moment from 'moment';

import { flexBoxCenter } from 'shared/styles/commonStyles';
import styled from 'styled-components';
import { selectEssSwitch } from 'shared/slice/essSwitchSlice';
import { selectTgsSwitch } from 'shared/slice/tgsSwitchSlice';
import { selectTesSwitch } from 'shared/slice/tesSwitchSlice';
import { selectUnits } from 'shared/slice/unitsSlice';
import { useMemo } from 'react';
import {
  chartTimeAxisTitle,
  dataConsumptionSeriesUnit,
  formatDateTooltip,
  formatDateXAxis,
  getConsumptionValue,
  toDataConsumption,
} from 'shared/helpers/helpers';
import { toClickedTimestamp } from './chartControls/chartClick';

// axisOnly renders just the y axis, for the pinned copy that stays put while
// the plot scrolls. hideYAxis suppresses the scrolling chart's own axis so the
// two are never shown at once.
const BarGraph = ({
  swtName,
  graph,
  location,
  machine,
  onPointClick,
  axisOnly = false,
  hideYAxis = false,
}) => {

  const dataType = graph === 'data consumption vs time';

  const { flatEssSwitch,flatTgsSwitch,flatTesSwitch } = useSelector(
    swtName === 'ess'
      ? selectEssSwitch
      : swtName === 'tgs'
        ? selectTgsSwitch
        : selectTesSwitch
  );
  const swtStatus = swtName === 'ess' ? flatEssSwitch : swtName === 'tgs' ? flatTgsSwitch : flatTesSwitch;
  const {
    graphDateData: { energyGasConsumptionGraphData, dataConsumptionGraphData, intervalUnit }
  } = swtStatus[location][machine];
  const { isF } = useSelector(selectUnits);
  const isGas = swtName === 'tgs';
  const configuration = swtName === 'ess' ? 'ess' : 'tgs/tes';
  const { locationName, machineName } = swtStatus[location][machine];

  const eneryGraphTitle = isGas ? 'gas consump.' : 'energy consump.';
  const tooltipTitle = dataType ? 'data consump.' : eneryGraphTitle;

  const gasUnit = isF ? 'FT³' : 'M³';
  const energyUnit = isGas ? gasUnit : 'kw';
  const graphData = dataType ? dataConsumptionGraphData : energyGasConsumptionGraphData;

  // The API reports data consumption in bytes. Every point on this axis has to
  // share one unit, so it is picked from the largest reading in the series.
  const unit = dataType
    ? dataConsumptionSeriesUnit(
        Array.isArray(graphData) ? graphData.map((data) => data[1]) : [],
      )
    : energyUnit;

  const toPlottedValue = (raw) =>
    dataType
      ? toDataConsumption(raw, unit)
      : getConsumptionValue(swtName, raw, isF);

  const sortedValues = (type) => {

    const uniqueValues = [...new Set(graphData.map(item => Math.floor(item[type])))];
    const sortedValues = uniqueValues.sort((a, b) => a - b);
    return sortedValues;
  };

  const dataGraph = useMemo(() => {
    if (Array.isArray(graphData)) {
      const result = graphData.map((data) => {
        return {
          name: data[0],
          consumption: toPlottedValue(data[1]),
          date: data[0],
        };
      });

      return result;
    }
    return [{
      name: null,
      consumption: null,
      date: null,
    }];
  }, [graphData]);

  const pointsY = useMemo(() => {
    if (Array.isArray(graphData)) {
      const sortedData = sortedValues(1);
      if (sortedValues.length > 0) {
        const newValues = sortedData.map(el => toPlottedValue(el));
        const valueRange = newValues[newValues.length - 1] - newValues[0];
        const valueIncrement = (valueRange / 5);
        const result = [];
        for (let i = 0; i <= 5; i++) {
          const value = newValues[0] + i * valueIncrement;
          result.push(dataType ? Number(value.toFixed(2)) : Math.ceil(value));
        }
        return result;
      }
      return [];
    }
    return [];
  }, [graphData]);

  const CustomTooltip = ({ active, payload, label }) => {
    const switchName = `${locationName} - ${machineName} - ${configuration}`;

    if (active && Array.isArray(payload)) {
      const payload1stRow = switchName;
      // Converting bytes to gb turns a whole number into a long float, so the
      // tooltip has to round it. Energy and gas arrive already rounded.
      const shownValue = dataType
        ? Number(payload[0]?.value).toFixed(2)
        : payload[0]?.value;
      const payload2ndRow = `${tooltipTitle} ${shownValue} ${unit}`;
      const payload3rdRow = `${moment(payload[0]?.payload?.name)
        .format(formatDateTooltip(dataType ? 'months' : intervalUnit))}`;

      return (
        <PayloadWrapper className='custom-tooltip'>
          <PayloadItem className='label'>{payload1stRow}</PayloadItem>
          <PayloadItem className='label'>{payload2ndRow}</PayloadItem>
          <PayloadItem className='label'>{payload3rdRow}</PayloadItem>
        </PayloadWrapper>
      );
    }
    return null;
  };

  const barColor = dataType
    ? '#FF0080'
    : swtName === 'ess' ? '#83FFFF' : swtName === 'tgs' ? '#ff7800' : '#95ff45';

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <BarChart
        width={100}
        height={100}
        data={dataGraph}
        onClick={(state) => {
          const timestamp = toClickedTimestamp(state);
          if (timestamp && onPointClick) {
            onPointClick(timestamp);
          }
        }}
      >
        {!axisOnly && (
          <Bar dataKey='consumption' fill={barColor} barSize={25} />
        )}

        {!axisOnly && <CartesianGrid stroke='#fff' />}


        <XAxis
          tick={axisOnly ? false : { fontSize: 8 }}
          stroke='#FFFFFF'
          dataKey='name'
          tickFormatter={
            (unixTime) => moment(unixTime)
              .format(formatDateXAxis(dataType ? 'months' : intervalUnit))
          }
          label={
            axisOnly
              ? undefined
              : {
                  value: chartTimeAxisTitle(dataType ? 'months' : intervalUnit),
                  position: 'BottomRight',
                  fill: '#ffff',
                  dx: 275,
                  dy: 10,
                  fontSize: 11,
                }
          }
        />

        <YAxis
          stroke='#FFFFFF'
          type='number'
          tick={{ fontSize: 11 }}
          ticks={pointsY}
          domain={['dataMin', 'dataMax']}
          unit={unit}
          hide={hideYAxis}
        />
        {!axisOnly && (
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={<CustomTooltip />}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BarGraph;

const PayloadWrapper = styled.div`
  ${flexBoxCenter}
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;

  background-color: #233a54;
  outline: 1px solid #1b2b44;
  padding: 5px;

  border-radius: 8px;
`;
const PayloadItem = styled.span`
  font-size: 12px;
  letter-spacing: 1.2px;
`;
